var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Actions, CalloutError, CalloutWarning, CalloutPending, CalloutInformation, CalloutNeutral, CalloutSuccess, ContentWrapper, DismissIcon, DismissIconWarning, IconContainer, InfoIcon, ErrorIcon, SuccessIcon, PendingIcon, Message, Title, WarningIcon, NeutralIcon, } from './Callout.styled';
import { getAutomation, getDebug } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { getChildrenByType, removeChildrenByType } from 'react-nanny';
import { BorderlessNotificationButton, BorderlessButton } from '@citrus/button';
import { isNotNullOrEmpty } from '@pcty/utils';
import { cx, css as emotionCss } from 'emotion';
import localizationMap from '../localization';
import { dismissButton as dismissButtonStyles, contentWrapperWithTitle as contentWrapperWithTitleStyles, actionsWithTitle as actionsWithTitleStyles, messageWithTitle as messageWithTitleStyles, } from '../styles/citron';
var VERSION = '4.2.0';
var Callout = React.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, hidden = _a.hidden, id = _a.id, onDismiss = _a.onDismiss, 
    // FIXME: check if `renderIcon` is needed
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderIcon = _a.renderIcon, title = _a.title, _f = _a.type, type = _f === void 0 ? 'information' : _f, _g = _a.__TYPE, TYPE = _g === void 0 ? 'Callout' : _g, other = __rest(_a, ["automationId", "children", "className", "css", "hidden", "id", "onDismiss", "renderIcon", "title", "type", "__TYPE"]);
    var lang = useLocalization(localizationMap);
    var isCitron = useTheme().citrus2022;
    if (hidden)
        return null;
    var getIcon = function (type, lang) {
        if (isCitron) {
            switch (type) {
                case 'information':
                default:
                    return React.createElement(InfoIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'success':
                    return React.createElement(SuccessIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'warning':
                    return React.createElement(WarningIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'error':
                    return React.createElement(ErrorIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'pending':
                    return React.createElement(PendingIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'neutral':
                    return React.createElement(NeutralIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
            }
        }
        else {
            switch (type) {
                case 'information':
                default:
                    return React.createElement(InfoIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
                case 'warning':
                    return React.createElement(WarningIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
            }
        }
    };
    var getCallout = function (type) {
        if (isCitron) {
            switch (type) {
                case 'neutral':
                default:
                    return CalloutNeutral;
                case 'information':
                    return CalloutInformation;
                case 'success':
                    return CalloutSuccess;
                case 'warning':
                    return CalloutWarning;
                case 'error':
                    return CalloutError;
                case 'pending':
                    return CalloutPending;
            }
        }
        else {
            switch (type) {
                case 'information':
                default:
                    return CalloutInformation;
                case 'warning':
                    return CalloutWarning;
            }
        }
    };
    var renderDismiss = function (automationId, id, type, lang, onDismiss) {
        if (!onDismiss || type === 'error')
            return null;
        if (isCitron) {
            return (React.createElement(BorderlessButton, __assign({ className: cx(emotionCss(dismissButtonStyles)), onClick: function () { return onDismiss(id); } }, getAutomation(id, 'dismiss')), type === 'warning' ? React.createElement(DismissIconWarning, { "aria-hidden": "true" }) : React.createElement(DismissIcon, { "aria-hidden": "true" })));
        }
        return (React.createElement(BorderlessNotificationButton, __assign({ "aria-label": lang.dismiss, color: "black", key: "".concat(id, "-dismiss-button"), onClick: function () { return onDismiss(id); }, size: "small" }, getAutomation(automationId, 'dismiss')), type === 'warning' ? React.createElement(DismissIconWarning, { "aria-hidden": "true" }) : React.createElement(DismissIcon, { "aria-hidden": "true" })));
    };
    Callout.defaultProps = {
        __TYPE: 'Callout',
    };
    var Component = getCallout(type);
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNullOrEmpty(css),
        _b), className || '');
    var message = removeChildrenByType(children, [
        'BorderlessButton',
        'Button',
        'NotificationButton',
        'PrimaryButton',
        'SecondaryButton',
    ]);
    return (React.createElement(Component, __assign({ className: classes, ref: ref, role: "status" }, other, getAutomation(automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION)),
        React.createElement(IconContainer, { key: "pcty-callout-icon-container-".concat(id) }, getIcon(type, lang)),
        React.createElement(ContentWrapper, { className: cx((_c = {},
                _c[emotionCss(contentWrapperWithTitleStyles)] = isNotNullOrEmpty(title) && isCitron,
                _c)) },
            React.createElement(Message, __assign({ key: "pcty-callout-message-".concat(id) }, getAutomation(automationId, 'message'), { className: cx((_d = {},
                    _d[emotionCss(messageWithTitleStyles)] = isNotNullOrEmpty(title) && isCitron,
                    _d)) }),
                isNotNullOrEmpty(title) && React.createElement(Title, null, title),
                typeof children === 'string' ? React.createElement("p", null, message) : message,
                !isCitron && (React.createElement("div", null, getChildrenByType(children, ['NotificationButton']).map(function (button) {
                    return React.cloneElement(button, { color: type === 'warning' ? 'red' : 'blue' });
                })))),
            isCitron && (React.createElement(Actions, { className: cx((_e = {},
                    _e[emotionCss(actionsWithTitleStyles)] = isNotNullOrEmpty(title),
                    _e)), key: "pcty-callout-actions-".concat(id) }, getChildrenByType(children, ['NotificationButton']).map(function (button) {
                return React.cloneElement(button, { color: type === 'warning' ? 'red' : 'blue', size: 'small' });
            })))),
        !isCitron && (React.createElement(Actions, { key: "pcty-callout-actions-".concat(id) }, renderDismiss(automationId !== null && automationId !== void 0 ? automationId : '', id, type, lang, onDismiss))),
        isCitron && renderDismiss(automationId !== null && automationId !== void 0 ? automationId : '', id, type, lang, onDismiss)));
});
export default Callout;
